import { snakeCaseToTitleCase } from '@agilelab/plugin-wb-platform-common';
import { WbBadge, WbTooltip } from '@agilelab/plugin-wb-platform';
import { Typography } from '@material-ui/core';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import CheckIcon from '@material-ui/icons/Check';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { FC, isValidElement, ReactNode } from 'react';
import {
  PartiallyDeployedIcon,
  ProvisioningProgressIcon,
} from '../ReleasePage/ReleaseTableColumns/icons';

type DeploymentStatusBadgeColor = 'success' | 'secondary' | 'accent' | 'error';
type DeploymentStatusBadgeVariant = 'soft' | 'bright' | 'stroke';

const getStatusBadgeAppearance = (
  status?: string,
): {
  color: DeploymentStatusBadgeColor;
  variant: DeploymentStatusBadgeVariant;
  icon: ReactNode;
} | null => {
  switch (status) {
    case 'DEPLOYED':
      return {
        color: 'success',
        variant: 'stroke',
        icon: <CheckIcon fontSize="small" />,
      };
    case 'NOT_DEPLOYED':
      return {
        color: 'secondary',
        variant: 'stroke',
        icon: <CancelOutlinedIcon fontSize="small" />,
      };
    case 'PARTIALLY_DEPLOYED':
      return {
        color: 'secondary',
        variant: 'stroke',
        icon: <PartiallyDeployedIcon fontSize="small" />,
      };
    case 'PROVISIONING_IN_PROGRESS':
      return {
        color: 'accent',
        variant: 'stroke',
        icon: <ProvisioningProgressIcon fontSize="small" />,
      };
    case 'CORRUPT':
      return {
        color: 'error',
        variant: 'stroke',
        icon: <ErrorOutlineIcon fontSize="small" />,
      };
    default:
      return null;
  }
};

// Generic badge component to show the deployment status of a release or data product, it can be used in different contexts and with different styles
export interface DeploymentStatusBadgeProps {
  status?: string;
  version?: string;
  label: ReactNode | string;
  disabled?: boolean;
  showIcon?: boolean;
  showVersion?: boolean;
  tooltipTitle?: string;
  to?: string;
}

export const DeploymentStatusBadge: FC<DeploymentStatusBadgeProps> = ({
  status,
  version,
  label,
  disabled,
  showIcon = true,
  showVersion,
  tooltipTitle,
  to,
}) => {
  const configuration = getStatusBadgeAppearance(status);
  const versionToShow = version?.replace('.0', '');

  const icon = showIcon ? configuration?.icon : undefined;

  const defaultLabel = (
    <Typography component="span" variant="caption">{`${snakeCaseToTitleCase(
      label as string,
    )} ${showVersion ? `(${versionToShow})` : ''}`}</Typography>
  );

  if (disabled) {
    return (
      <WbBadge variant="disabled" startIcon={icon}>
        {isValidElement(label) ? label : defaultLabel}
      </WbBadge>
    );
  }

  const badge = (
    <WbBadge
      startIcon={icon}
      to={to}
      color={configuration?.color}
      variant={configuration?.variant}
    >
      {isValidElement(label) ? label : defaultLabel}
    </WbBadge>
  );

  if (!tooltipTitle) {
    return badge;
  }

  return <WbTooltip title={tooltipTitle}>{badge}</WbTooltip>;
};
